/* Purry Home — Lux product page behaviours.
   Vanilla, deferred. Each feature is a small function that no-ops when its
   markup is absent, so sections can be added or removed in the editor freely.
   Dawn's own product-info.js publishes PUB_SUB_EVENTS.variantChange; we
   subscribe rather than re-implement variant logic. */
(() => {
  'use strict';

  // Text from the locale files (layout/theme.liquid → window.phI18n); English if missing.
  // English pages keep en-GB date and number formats.
  const T = (k, en) => (window.phI18n && window.phI18n[k]) || en;
  const LANG = /^en\b/i.test(document.documentElement.lang || 'en') ? 'en-GB' : document.documentElement.lang;

  const onReady = (fn) => (document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn());

  // Subscribe to Dawn's variant change bus when it exists.
  const onVariantChange = (fn) => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, fn);
    }
  };

  // Size pills show the price of the variant they would select, given the
  // other options as currently chosen. Dawn re-renders the picker on every
  // change, so we re-annotate after each variantChange.
  function annotateSizePrices() {
    document.querySelectorAll('[data-ph-size-prices]').forEach((picker) => {
      const data = picker.querySelector('script[data-ph-variants]');
      if (!data) return;
      let variants;
      try { variants = JSON.parse(data.textContent); } catch (e) { return; }
      const optIndex = Number(picker.dataset.phSizePrices);
      const selects = picker.closest('variant-selects');
      if (!selects) return;
      const chosen = [];
      selects.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group, i) => {
        const r = group.querySelector('input[type=radio]:checked');
        const s = group.querySelector('select');
        chosen[i] = r ? r.value : (s ? s.value : null);
      });
      picker.querySelectorAll('input[type=radio]').forEach((radio) => {
        const want = chosen.slice();
        want[optIndex] = radio.value;
        const v = variants.find((x) => x.options.every((o, i) => want[i] == null || o === want[i]));
        const label = picker.querySelector(`label[for="${radio.id}"] .ph-pill__price`);
        if (label) label.textContent = v ? v.price : '';
      });
    });
  }

  // "Pick your size" cards: each shows the price/stock of the variant it
  // would select given the other options as chosen; the button selects it in
  // the picker and scrolls back to the buy box.
  function sizeCards() {
    const root = document.querySelector('[data-ph-size-cards]');
    const pdp = document.querySelector('product-info[data-section]');
    if (!root || !pdp) return;
    const idx = Number(root.dataset.phSizeCards);
    let variants;
    try { variants = JSON.parse(root.querySelector('script[data-ph-variants]').textContent); } catch (e) { return; }
    const groups = () => {
      const sel = pdp.querySelector('variant-selects');
      return sel ? [...sel.querySelectorAll('fieldset, .product-form__input--dropdown')] : [];
    };
    const chosen = () => groups().map((g) => {
      const r = g.querySelector('input[type=radio]:checked');
      const s = g.querySelector('select');
      return r ? r.value : (s ? s.value : null);
    });
    const paint = () => {
      const c = chosen();
      root.querySelectorAll('[data-ph-size-card]').forEach((card) => {
        const val = card.dataset.phSizeCard;
        const want = c.slice();
        want[idx] = val;
        const v = variants.find((x) => x.options.every((o, i) => want[i] == null || o === want[i]))
          || variants.find((x) => x.options[idx] === val);
        if (v) {
          const p = card.querySelector('[data-price]'); if (p) p.textContent = v.price;
          const cmp = card.querySelector('[data-compare]'); if (cmp) { cmp.textContent = v.compare || ''; cmp.hidden = !v.compare; }
          const a = card.querySelector('[data-avail]'); if (a) { a.textContent = v.available ? T('in_stock', 'In stock') : T('sold_out', 'Sold out'); a.classList.toggle('is-out', !v.available); }
        }
        card.classList.toggle('is-selected', c[idx] === val);
      });
    };
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ph-select-size]');
      if (!btn) return;
      const val = btn.closest('[data-ph-size-card]').dataset.phSizeCard;
      const g = groups()[idx];
      if (!g) return;
      const radio = [...g.querySelectorAll('input[type=radio]')].find((r) => r.value === val);
      if (radio) { if (!radio.checked) radio.click(); }
      else { const s = g.querySelector('select'); if (s) { s.value = val; s.dispatchEvent(new Event('change', { bubbles: true })); } }
      const target = pdp.querySelector('.ph-pdp__title') || g;
      const top = target.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: 'smooth' });
      paint();
    });
    paint();
    onVariantChange(() => setTimeout(paint, 0));
  }

  // Sticky bar: shows once the buy button has scrolled above the viewport,
  // submits the main product form, mirrors Dawn's price block on change.
  function stickyBar() {
    const bar = document.querySelector('[data-ph-sticky]');
    if (!bar) return;
    const pdp = document.querySelector('product-info[data-section]');
    // The installment form also starts with "product-form-"; take the add-to-cart one.
    const form = pdp && (pdp.querySelector('form[data-type="add-to-cart-form"]') || pdp.querySelector('product-form form'));
    const cta = bar.querySelector('[data-ph-sticky-cta]');
    if (!form || !cta) { bar.remove(); return; }
    // getAttribute: the form's <input name="id"> shadows form.id.
    cta.setAttribute('form', form.getAttribute('id'));
    document.body.classList.add('ph-has-sticky');

    const watch = pdp.querySelector(bar.dataset.watch || '.product-form__submit');
    if (watch) {
      // Scroll-driven rather than IntersectionObserver: IO stops reporting on
      // mobile Chrome while a third-party popup holds a body scroll-lock.
      let ticking = false;
      const check = () => {
        ticking = false;
        const on = watch.getBoundingClientRect().bottom < 0;
        if (bar.classList.contains('is-on') !== on) {
          bar.classList.toggle('is-on', on);
          bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        }
      };
      const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      check();
    }

    const priceEl = bar.querySelector('[data-ph-sticky-price]');
    const variantEl = bar.querySelector('[data-ph-sticky-variant]');
    const imgEl = bar.querySelector('[data-ph-sticky-img]');
    const sync = () => {
      const src = pdp.querySelector('.price .price__sale .price-item--sale, .price .price__regular .price-item--regular');
      if (src && priceEl) priceEl.textContent = src.textContent.trim();
      const submit = pdp.querySelector('.product-form__submit');
      if (submit) {
        cta.disabled = submit.disabled;
        const label = submit.querySelector('span');
        if (label) cta.textContent = label.textContent.trim();
      }
    };
    onVariantChange((e) => {
      const v = e && e.data && e.data.variant;
      if (v && variantEl) variantEl.textContent = v.title || '';
      if (v && imgEl && v.featured_image && v.featured_image.src) {
        imgEl.src = v.featured_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x120_crop_center$1');
      }
      setTimeout(sync, 0);
    });
    sync();
  }

  // "Print specifications" prints only the spec table.
  function printSpecs() {
    document.querySelectorAll('[data-ph-print]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const table = document.querySelector(btn.dataset.phPrint);
        if (!table) return;
        const w = window.open('', '_blank', 'width=720,height=900');
        if (!w) return;
        const title = document.querySelector('h1') ? document.querySelector('h1').textContent.trim() : document.title;
        w.document.write(`<!doctype html><title>${T('print_title', '__T__ — specifications').replace('__T__', title)}</title><style>body{font:14px/1.5 -apple-system,Helvetica,Arial,sans-serif;padding:32px;color:#1c1a17}h1{font-size:20px;margin:0 0 16px}dl{display:grid;grid-template-columns:200px 1fr}dt,dd{margin:0;padding:6px 0;border-bottom:1px solid #e4ded5}dt{color:#8a8176;text-transform:uppercase;font-size:11px;letter-spacing:.06em}</style><h1>${title}</h1>${table.outerHTML}<p style="margin-top:24px;color:#8a8176;font-size:12px">purryhome.com</p>`);
        w.document.close();
        w.focus();
        w.print();
      });
    });
  }

  // Gallery: "See all N photos" past the thumbnail cap, and a light on/off
  // toggle when media are tagged with alt text "lit" / "unlit".
  function gallery() {
    const gal = document.querySelector('product-info media-gallery');
    if (!gal) return;
    const thumbs = gal.querySelector('[id^="GalleryThumbnails"]');
    const list = thumbs && thumbs.querySelector('.thumbnail-list');
    const CAP = 6;  // thumbnails before "See all N photos" (column level with the main image); CSS nth-child(n + 7) matches
    if (list && !thumbs.querySelector('.ph-gallery__more')) {
      const total = list.querySelectorAll('.thumbnail-list__item').length;
      if (total > CAP) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ph-gallery__more';
        btn.innerHTML = `<svg class="ph-i" viewBox="0 0 256 256" fill="none" stroke="currentColor" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"><rect x="56" y="72" width="168" height="136" rx="8"/><path d="M56 176l48-48 40 40 24-24 56 56"/></svg><span>${T('see_all', 'See all __N__ photos').replace('__N__', total)}</span>`;
        btn.addEventListener('click', () => {
          const open = list.classList.toggle('is-expanded');
          btn.querySelector('span').textContent = open ? T('show_fewer', 'Show fewer') : T('see_all', 'See all __N__ photos').replace('__N__', total);
        });
        thumbs.appendChild(btn);
      }
    }
    const viewer = gal.querySelector('[id^="GalleryViewer"]');
    if (!viewer || viewer.querySelector('.ph-lit-toggle')) return;
    const slides = [...viewer.querySelectorAll('[data-media-id]')];
    const find = (tag) => slides.find((s) => {
      const img = s.querySelector('img');
      return img && new RegExp(`(^|\\b)${tag}(\\b|$)`, 'i').test(img.alt || '');
    });
    const lit = find('lit'), unlit = find('unlit');
    if (!lit || !unlit) return;
    const wrap = document.createElement('div');
    wrap.className = 'ph-lit-toggle';
    wrap.innerHTML = `<button type="button" data-on aria-pressed="true">${T('light_on', 'Light on')}</button><button type="button" data-off aria-pressed="false">${T('light_off', 'Light off')}</button>`;
    viewer.appendChild(wrap);
    const go = (slide, onBtn) => {
      if (typeof gal.setActiveMedia === 'function') gal.setActiveMedia(slide.dataset.mediaId, false);
      wrap.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', b === onBtn ? 'true' : 'false'));
    };
    wrap.querySelector('[data-on]').addEventListener('click', (e) => go(lit, e.currentTarget));
    wrap.querySelector('[data-off]').addEventListener('click', (e) => go(unlit, e.currentTarget));
  }

  // Reviews: client-side sort, show more, write-a-review toggle.
  function reviews() {
    const root = document.querySelector('[data-ph-reviews]');
    if (!root) return;
    const list = root.querySelector('[data-ph-list]');
    const perPage = Number(root.dataset.perPage || 6);
    let shown = perPage;
    const items = () => [...(list ? list.querySelectorAll('.ph-review') : [])];
    const paint = () => items().forEach((li, i) => li.classList.toggle('is-hidden', i >= shown));
    const more = root.querySelector('[data-ph-more]');
    // Only the first page is in the HTML (page weight); the full list is fetched once, on demand.
    const tpl = root.querySelector('[data-ph-review-tpl]');
    const total = Number(root.dataset.phTotal || 0);
    let loaded = !root.dataset.phSrc || !tpl || items().length >= total;
    let loading = null;
    const esc = (s) => String(s == null ? '' : s);
    const fmtDate = (d) => { const t = Date.parse(d); return isNaN(t) ? '' : new Date(t).toLocaleDateString(LANG, { day: 'numeric', month: 'short', year: 'numeric' }); };
    const render = (r) => {
      const li = tpl.content.firstElementChild.cloneNode(true);
      li.dataset.rating = r.rating; li.dataset.date = Math.floor((Date.parse(r.date) || 0) / 1000); li.dataset.photos = (r.photos || []).length;
      const stars = Math.max(0, Math.min(5, Number(r.rating) || 0));
      const fill = li.querySelector('.ph-stars__row--fill'); if (fill) { fill.style.setProperty('--ph-full', Math.floor(stars)); fill.style.setProperty('--ph-frac', (stars - Math.floor(stars)).toFixed(2)); }
      const set = (f, v) => { const el = li.querySelector(`[data-f="${f}"]`); if (el) el.textContent = esc(v); return el; };
      set('date', fmtDate(r.date)); set('body', r.body); set('author', r.author);
      const t = set('title', r.title); if (t && !r.title) t.remove();
      const pr = li.querySelector('[data-f="product"]'); if (pr) { if (r.product_title) { pr.textContent = r.product_title; pr.href = '/products/' + (r.product || ''); } else pr.parentElement.remove(); }
      const loc = set('location', r.location ? ' · ' + r.location : ''); if (loc && !r.location) loc.remove();
      const ver = li.querySelector('[data-f="verified"]'); if (ver && !r.verified) ver.remove();
      const ph = li.querySelector('[data-f="photos"]'); if (ph) { if ((r.photos || []).length) { ph.hidden = false; ph.innerHTML = r.photos.map((u) => `<li><img src="${esc(u).replace(/"/g, '')}" alt="${T('photo_from', 'Photo from __A__').replace('__A__', esc(r.author)).replace(/"/g, '')}" loading="lazy" width="96" height="96"></li>`).join(''); } else ph.remove(); }
      return li;
    };
    const ensureLoaded = () => {
      if (loaded) return Promise.resolve();
      if (loading) return loading;
      loading = fetch(root.dataset.phSrc, { credentials: 'same-origin' }).then((x) => x.text()).then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[data-ph-reviews-json]');
        const all = node ? JSON.parse(node.textContent) : [];
        const have = new Set(items().map((li) => li.dataset.id));
        all.forEach((r) => { if (!have.has(String(r.id))) { const li = render(r); li.dataset.id = r.id; list.appendChild(li); } });
        loaded = true;
      }).catch(() => { loaded = true; });
      return loading;
    };
    if (more) more.addEventListener('click', () => ensureLoaded().then(() => { shown += perPage; paint(); if (shown >= items().length) more.hidden = true; }));
    const sort = root.querySelector('[data-ph-sort]');
    if (sort && list) sort.addEventListener('change', () => ensureLoaded().then(() => {
      const by = sort.value;
      const arr = items();
      arr.sort((a, b) => {
        const ra = +a.dataset.rating, rb = +b.dataset.rating, da = +a.dataset.date, db = +b.dataset.date;
        if (by === 'highest') return rb - ra || db - da;
        if (by === 'lowest') return ra - rb || db - da;
        if (by === 'photos') return (+b.dataset.photos > 0) - (+a.dataset.photos > 0) || db - da;
        return db - da;
      });
      arr.forEach((li) => list.appendChild(li));
      shown = perPage; paint(); if (more) more.hidden = shown >= arr.length;
    }));
    const write = root.querySelector('[data-ph-write]');
    const form = root.querySelector('[data-ph-form]');
    if (write && form) write.addEventListener('click', () => {
      form.hidden = !form.hidden;
      if (!form.hidden) { form.scrollIntoView({ behavior: 'smooth', block: 'start' }); const f = form.querySelector('input[type=text]'); if (f) setTimeout(() => f.focus(), 400); }
    });
    // Keep the form open after a submit round-trip so the message is visible.
    if (form && (form.querySelector('.ph-form__ok') || form.querySelector('.ph-form__err'))) form.hidden = false;
  }

  // Recently viewed: localStorage only, written on every product page,
  // rendered into the ph-recently-viewed section when 2+ other items exist.
  function recentlyViewed() {
    const KEY = 'ph_rv';
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } };
    const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ } };
    const money = (cents) => {
      const fmt = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
      try { return new Intl.NumberFormat(LANG, { style: 'currency', currency: fmt, minimumFractionDigits: 2 }).format(cents / 100); }
      catch (e) { return '£' + (cents / 100).toFixed(2); }
    };
    const cur = document.querySelector('[data-ph-current]');
    let current = null;
    if (cur) { try { current = JSON.parse(cur.textContent); } catch (e) {} }
    let list = read();
    if (current && current.handle) {
      list = [{ h: current.handle, t: current.title, i: current.featured_image, p: current.price, c: current.compare_at_price, u: current.url, ts: Date.now() }]
        .concat(list.filter((x) => x.h !== current.handle)).slice(0, 12);
      write(list);
    }
    const sec = document.querySelector('[data-ph-recent]');
    if (!sec) return;
    const limit = Number(sec.dataset.limit || 6);
    const others = list.filter((x) => !current || x.h !== current.handle).slice(0, limit);
    if (others.length < 2) return;
    const ul = sec.querySelector('[data-ph-recent-list]');
    ul.innerHTML = others.map((x) => {
      const img = x.i ? x.i.replace(/(\.[a-z]+)(\?.*)?$/i, '_600x600_crop_center$1') : '';
      const cmp = x.c && x.c > x.p ? `<s>${money(x.c)}</s>` : '';
      return `<li><a class="ph-recent__card" href="${x.u}">${img ? `<img src="${img}" alt="" loading="lazy" width="300" height="300">` : ''}<span class="t">${x.t}</span><span class="p">${cmp}${money(x.p)}</span></a></li>`;
    }).join('');
    sec.hidden = false;
  }

  // Overview copy hygiene for pasted supplier HTML: shouting headings get
  // sentence case (acronyms kept), empty headings/paragraphs are dropped.
  function tidyOverview() {
    const KEEP = /^(LED|UK|US|EU|USB|RGB|RGBW|CCT|DC|AC|IP\d{2}|E\d{2}|G\d|GU10|B22|3D|CRI|AAA?|TV|DIY|UV)$/i;
    document.querySelectorAll('.ph-overview :is(h1,h2,h3,h4,h5,p)').forEach((el) => {
      if (!el.textContent.trim() && !el.querySelector('img,video,iframe')) { el.remove(); return; }
      if (!/^H[1-5]$/.test(el.tagName)) return;
      const t = el.textContent.replace(/\s+/g, ' ').trim().replace(/:$/, '');
      if (t.length < 13 || t !== t.toUpperCase() || !/[A-Z]/.test(t)) { if (el.textContent.trim() !== t) el.textContent = t; return; }
      const words = t.toLowerCase().split(' ').map((w) => {
        const core = w.replace(/[^a-z0-9]/g, '');
        return KEEP.test(core) ? w.replace(core, core.toUpperCase()) : w;
      });
      words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
      el.textContent = words.join(' ');
    });
  }

  onReady(() => {
    tidyOverview();
    annotateSizePrices();
    onVariantChange(() => setTimeout(annotateSizePrices, 0));
    sizeCards();
    stickyBar();
    printSpecs();
    gallery();
    reviews();
    recentlyViewed();
  });
})();
